/* eslint-disable no-nested-ternary */
import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Typography } from '../typography/Typography';
import { Stack } from '../stack/Stack';
import { Button, ButtonProps } from '../button';
import { Divider } from '../divider/Divider';
import { ArrowRight, Minus, TickCircle } from '../icon';
import { useBackgroundColor } from '../../utils';

// Interface
export type iconBenefitsType = 'tick' | 'minus';

export interface ProductCardSimpleProps {
  heading?: string;
  coverPriceLabel?: string;
  coverPriceWeekly?: string;
  actionLabel?: string;
  actionUrl?: string;
  vetFee?: string;
  excess?: string;
  benefits?: Benefit[];
  id: string;
  button?: ButtonProps;
}

export interface Benefit {
  component: {
    text: string;
    icon: 'Tick' | 'Minus';
  };
}

// Styles

const StyledHeader = styled(Stack)`
  ${({ theme: { space } }) => css`
    padding: ${space.fluidMedium} ${space.fluidSmall};
    ${useBackgroundColor('brandOne100')};
    display: flex;
    flex-direction: column;
    height: 100%;
    flex: 0;
  `};
`;

const StyledHeading = styled(Stack)`
  flex-grow: 1;
`;

const StyledProductCardSimple = styled.div`
  ${({ theme: { colors, borderRadii } }) => css`
    border: 1px solid ${colors.brandOne80};
    border-radius: ${borderRadii.medium};
    overflow: hidden;
    display: flex;
    flex-direction: column;
  `};
`;

const StyledFees = styled(Typography)`
  ${({ theme: { space } }) => css`
    display: flex;
    align-items: center;
    gap: ${space.xSmall};
  `};
`;

const StyledCta = styled(Button)`
  ${({ theme: { space } }) => css`
    width: 100%;
    min-height: 64px; // Stop button jumping when adding/removing icon
    margin-top: ${space.medium};
  `};
`;

const StyledCtaContainer = styled.div`
  margin-top: auto !important;
`;

const StyledBody = styled(Stack)`
  ${({ theme: { space } }) => css`
    padding: ${space.fluidMedium} ${space.fluidSmall};
  `};
`;

const StyledList = styled.ul`
  ${({ theme: { space } }) => css`
    display: grid;
    gap: ${space.xxSmall};
    list-style: none;
    li {
      display: flex;
      align-items: center;
      gap: ${space.small};
    }
  `};
`;

const StyledFooter = styled(Stack)`
  ${({ theme: { space, media } }) => css`
    padding: 0 ${space.medium} ${space.medium} ${space.medium};
    ${StyledCta} {
      margin-top: ${space.xxSmall};
    }
    @media (min-width: ${media.medium}) {
      margin-top: auto;
    }
  `};
`;

// JSX

export const ProductCardSimple: FC<ProductCardSimpleProps> = ({
  heading,
  coverPriceLabel,
  coverPriceWeekly,
  button,
  vetFee,
  excess,
  benefits,
  id,
  ...rest
}) => (
  <StyledProductCardSimple {...rest} id={id} data-test="product-card">
    <StyledHeader gap="medium">
      <StyledHeading gap="xSmall">
        {heading && (
          <Typography component="h2" variant="headingMedium">
            {heading}
          </Typography>
        )}
      </StyledHeading>
      {coverPriceWeekly && (
        <Stack gap="xxSmall">
          {coverPriceLabel && (
            <Typography component="p" variant="bodyMedium">
              {coverPriceLabel}
            </Typography>
          )}
          {coverPriceWeekly && (
            <Typography component="p" variant="headingMedium">
              {coverPriceWeekly}{' '}
              <Typography component="span" variant="bodyMedium">
                per month
              </Typography>
            </Typography>
          )}
        </Stack>
      )}

      {/* {button && (
        <StyledCtaContainer>
          <StyledCta
            id={`action-${id}-top`}
            variant="primary"
            url={button.url}
            type="button"
            iconRight={<ArrowRight />}
          >
            {button.label}
          </StyledCta>
        </StyledCtaContainer>
      )} */}
    </StyledHeader>
    <StyledBody gap="medium">
      <Stack gap="medium">
        {vetFee && (
          <>
            <Stack gap="xxSmall">
              {vetFee && (
                <StyledFees component="p" variant="headingSmall">
                  <TickCircle color="brandTwo100" /> {vetFee}
                </StyledFees>
              )}
              {excess && (
                <StyledFees component="p" variant="headingSmall">
                  <TickCircle color="brandTwo100" /> {excess}
                </StyledFees>
              )}
            </Stack>
            <Divider />
          </>
        )}

        {benefits && (
          <StyledList>
            {benefits.map((benefit) => (
              <li key={benefit.component.text}>
                {benefit.component.icon === 'Tick' ? (
                  <TickCircle color="brandTwo100" />
                ) : benefit.component.icon === 'Minus' ? (
                  <Minus color="brandTwo100" />
                ) : null}
                {benefit.component.text}
              </li>
            ))}
          </StyledList>
        )}
        <Divider />
      </Stack>
    </StyledBody>
    <StyledFooter>
      {button && (
        <StyledCtaContainer>
          <StyledCta
            id={`action-${id}-bottom`}
            variant="primary"
            url={button.url}
            type="button"
            iconRight={<ArrowRight />}
          >
            {button.label}
          </StyledCta>
        </StyledCtaContainer>
      )}
    </StyledFooter>
  </StyledProductCardSimple>
);

export default ProductCardSimple;
